/**
 * The moderation queue, with a fixture path for `?moderationData=mock`.
 *
 * Live and mock satisfy one return shape, so the UI never branches on data
 * source. The page always sends an explicit `statuses` list — the combined
 * "needs moderation" view (AI_FLAGGED + PENDING_REVIEW + NEEDS_REVIEW) by
 * default, one status to browse — rather than leaning on Brooklyn's no-status
 * default. The ledger filters (`source`, `category`, `minTopScore`) are sent as
 * arguments and applied server-side before pagination, never to one fetched
 * page; the mock composes them the same way the SQL does.
 *
 * Two human actions: `resolve` (Approve / Reject) and `setStatus` (any human
 * status — the row's Set status menu, including "Re-run AI review"). Both refetch
 * afterwards; a row leaves the view when its new status is outside `statuses`.
 * A reload that fails after the decision was saved rejects with
 * `ModerationReloadError`, so the caller never reports it as a failed decision.
 *
 * Fixtures are reached only through the dynamic `import()` below, and they live
 * under `lib/gui/components/moderation-queue/mock/` — not next to this hook —
 * because `lib/vite/build/manual-chunks.js` assigns every `lib/services/**`
 * module to the `common` chunk, which overrides the code-splitting and would
 * ship the fixtures to every admin page. Under `lib/gui/` the import gets its
 * own lazy chunk; re-verify that against the built bundles' `.js.map` sources
 * after a production build rather than asserting it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CommentStatus,
  type ModerationCategory,
  ModerationResolveAction,
  type ModerationSource,
  ModerationSurface,
} from '../../graphql-types'
import {
  useModerationQueueQuery,
  type ModerationQueueQuery,
} from './queries/ModerationQueueQuery.generated'
import { useModerationResolveMutation } from './queries/ModerationResolveMutation.generated'
import { useModerationSetStatusMutation } from './queries/ModerationSetStatusMutation.generated'
import { ModerationReloadError } from './errors'
import useModerationMockMode from './useModerationMockMode'

/** One row of the queue, exactly as the query selects it. */
export type ModerationQueueItem = ModerationQueueQuery['moderationQueue']['items'][number]
export type ModerationQueueScore = ModerationQueueItem['scores'][number]
export type ModerationQueueComment = ModerationQueueItem['comment']

export const MODERATION_QUEUE_SURFACE = ModerationSurface.Comment
export const MODERATION_QUEUE_PAGE_SIZE = 50
/** Mock actions settle after this long, so overlapping actions on two rows can be checked by hand. */
export const MOCK_ACTION_DELAY_MS = 800

export interface UseModerationQueueOptions {
  page: number
  pageSize: number
  /**
   * Always sent. The page defaults to the combined "needs moderation" list and
   * the Status select swaps it for one status, so the API's own no-status
   * default never applies.
   */
  statuses: CommentStatus[]
  /** Narrow the queue to one author. */
  authorUid?: number
  /**
   * Look up one specific comment — the wire's `itemId` (the moderated item's
   * own id, not the video's `comment.contentId`).
   */
  commentId?: string
  /** Only comments posted in this video/circle — the wire's `containerId` (`comments.content_id`). */
  containerId?: string
  /** Only items whose latest queue-source row (AI or member report) matches. */
  source?: ModerationSource
  /** Only items whose applicable AI verdict landed in this category (NONE = every score was 0). */
  category?: ModerationCategory
  /**
   * Only items whose `topScore` is at least this 0–1 fraction. A threshold
   * excludes rows with no AI score — null is never >= anything, the way the SQL
   * `top_score >= $n` drops NULLs — so member reports and never-classified
   * comments vanish under any threshold, by design.
   */
  minTopScore?: number
}

export interface UseModerationQueueResult {
  items: ModerationQueueItem[]
  total: number
  loading: boolean
  error: Error | null
  resolve: (itemId: string, action: ModerationResolveAction) => Promise<void>
  setStatus: (itemId: string, status: CommentStatus) => Promise<void>
  refetch: () => Promise<void>
  isMock: boolean
}

/** Shared so an empty result keeps a stable identity across renders. */
const NO_ITEMS: ModerationQueueItem[] = []

const delay = (ms: number): Promise<void> =>
  new Promise((resolveDelay) => {
    setTimeout(resolveDelay, ms)
  })

/** What Brooklyn's REJECT lands on: a live-then-reported comment is taken down, anything else is denied. */
function mockRejectTarget (status: CommentStatus | null | undefined): CommentStatus {
  return status === CommentStatus.NeedsReview ? CommentStatus.Unpublished : CommentStatus.Rejected
}

export default function useModerationQueue ({
  page,
  pageSize,
  statuses,
  authorUid,
  commentId,
  containerId,
  source,
  category,
  minTopScore,
}: UseModerationQueueOptions): UseModerationQueueResult {
  const mockMode = useModerationMockMode()
  const isMock = mockMode !== null

  const [mockItems, setMockItems] = useState<ModerationQueueItem[] | null>(null)
  const [mockError, setMockError] = useState<Error | null>(null)

  const loadMockQueue = useCallback(async (): Promise<void> => {
    const fixtures = await import('../../gui/components/moderation-queue/mock/fixtures')
    if (mockMode === 'mock-error') {
      setMockError(fixtures.MOCK_QUEUE_ERROR)
      return
    }
    setMockError(null)
    setMockItems(fixtures.buildMockQueueItems())
  }, [mockMode])

  useEffect(() => {
    if (!isMock || mockMode === 'mock-loading') return
    // A failed chunk fetch has to surface as an error rather than as a preview
    // that loads forever.
    loadMockQueue().catch(() => {
      setMockError(new Error('Mock: failed to load the moderation queue fixtures'))
    })
  }, [isMock, mockMode, loadMockQueue])

  // The same view of the fixtures the API would answer with: the statuses the
  // page asked for, and the browse filters composed conjunctively the way the
  // SQL would be.
  const visibleMockItems = useMemo(() => {
    let visible = (mockItems ?? NO_ITEMS).filter(
      (item) => item.comment?.status != null && statuses.includes(item.comment.status),
    )
    if (authorUid !== undefined) {
      visible = visible.filter((item) => item.authorUid === authorUid)
    }
    if (commentId !== undefined) {
      visible = visible.filter((item) => item.itemId === commentId)
    }
    if (containerId !== undefined) {
      visible = visible.filter((item) => String(item.comment?.contentId ?? '') === containerId)
    }
    if (source !== undefined) {
      visible = visible.filter((item) => item.source === source)
    }
    if (category !== undefined) {
      visible = visible.filter((item) => item.category === category)
    }
    if (minTopScore !== undefined) {
      visible = visible.filter((item) => item.topScore != null && item.topScore >= minTopScore)
    }
    return visible
  }, [mockItems, statuses, authorUid, commentId, containerId, source, category, minTopScore])

  const { data, loading, error, refetch } = useModerationQueueQuery({
    variables: {
      surface: MODERATION_QUEUE_SURFACE,
      statuses,
      containerId,
      source,
      category,
      minTopScore,
      authorUid,
      itemId: commentId,
      limit: pageSize,
      offset: page * pageSize,
    },
    // So `refetch()` drives `loading` and the Refresh control responds.
    notifyOnNetworkStatusChange: true,
    // Mock mode must fire no network at all — see the mock README.
    skip: isMock,
  })

  const [resolveModeration] = useModerationResolveMutation()
  const [setModerationStatus] = useModerationSetStatusMutation()

  const refetchQueue = useCallback(async (): Promise<void> => {
    if (isMock) {
      await loadMockQueue()
      return
    }
    await refetch()
  }, [isMock, loadMockQueue, refetch])

  /**
   * After a saved decision. A failed reload must not read as a failed decision —
   * the row would be re-decided — so it is flagged for the caller to word apart.
   */
  const reloadAfterAction = useCallback(async (): Promise<void> => {
    try {
      await refetch()
    } catch (e) {
      throw new ModerationReloadError(e)
    }
  }, [refetch])

  /** Mock: move the row to its new status after the delay; it leaves the view if that status is not selected. */
  const setMockStatus = useCallback(
    async (
      itemId: string,
      next: (current: CommentStatus | null | undefined) => CommentStatus,
    ): Promise<void> => {
      await delay(MOCK_ACTION_DELAY_MS)
      setMockItems((current) =>
        (current ?? NO_ITEMS).map((item) =>
          item.itemId === itemId && item.comment
            ? { ...item, comment: { ...item.comment, status: next(item.comment.status) } }
            : item,
        ),
      )
    },
    [],
  )

  const resolve = useCallback(
    async (itemId: string, action: ModerationResolveAction): Promise<void> => {
      if (isMock) {
        await setMockStatus(itemId, (current) =>
          action === ModerationResolveAction.Approve
            ? CommentStatus.Published
            : mockRejectTarget(current),
        )
        return
      }
      await resolveModeration({
        variables: { surface: MODERATION_QUEUE_SURFACE, itemId, action },
      })
      await reloadAfterAction()
    },
    [isMock, setMockStatus, resolveModeration, reloadAfterAction],
  )

  const setStatus = useCallback(
    async (itemId: string, status: CommentStatus): Promise<void> => {
      if (isMock) {
        await setMockStatus(itemId, () => status)
        return
      }
      await setModerationStatus({
        variables: { surface: MODERATION_QUEUE_SURFACE, itemId, status },
      })
      await reloadAfterAction()
    },
    [isMock, setMockStatus, setModerationStatus, reloadAfterAction],
  )

  return useMemo(() => {
    if (isMock) {
      return {
        items: visibleMockItems.slice(page * pageSize, (page + 1) * pageSize),
        total: visibleMockItems.length,
        loading: mockMode === 'mock-loading' || (!mockItems && !mockError),
        error: mockError,
        resolve,
        setStatus,
        refetch: refetchQueue,
        isMock: true,
      }
    }
    return {
      items: data?.moderationQueue.items ?? NO_ITEMS,
      total: data?.moderationQueue.total ?? 0,
      loading,
      error: error ?? null,
      resolve,
      setStatus,
      refetch: refetchQueue,
      isMock: false,
    }
  }, [
    isMock,
    mockMode,
    mockItems,
    visibleMockItems,
    mockError,
    page,
    pageSize,
    data,
    loading,
    error,
    resolve,
    setStatus,
    refetchQueue,
  ])
}
